import React, { useRef, useEffect, useState } from 'react';
import { Box, DialogActions, DialogContent, Grid, TextField } from "@mui/material";

import { ILineItem, IQuote } from "helpers/interfaces";
import OKCancelControl from "components/Common/OKCancelControl";
import ErrorAdornment from "components/Common/ErrorAdornment";
import { useTranslations } from "@fenetech/translations";
import useMessageBox from "helpers/context/Page/useMessageBox";
import useCurrencyFormatter from "helpers/hooks/useCurrencyFormatter";
import Constants from "helpers/constants";

interface IProps {
    quote: IQuote;
    lineItem: ILineItem;
    onCancel: () => void;
    onSubmit: (lineItem: ILineItem, newPrice: number) => void;
}

const LineItemPrice: React.FC<IProps> = ({ quote, lineItem, onCancel, onSubmit }: IProps) => {

    const tm = useTranslations();
    const cf = useCurrencyFormatter(quote.currencyCulture);
    const messageBox = useMessageBox();

    const [value, setValue] = useState<string>(cf.Format(lineItem.unitPrice));
    const valueInput = useRef<HTMLInputElement>(null);
    const [valueErrorMessage, setValueErrorMessage] = useState<string>("");


    useEffect(() => {
        if (valueInput?.current) {
            valueInput.current.focus();
            valueInput.current.select();
        }
    }, [valueInput]);

    const handleValueChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setValue(e.target.value);
    }

    const validatePrice = React.useCallback((value: string) => {
        var numberPrice = cf.Parse(value);
        if (value === "") {
            return tm.Get("Price must be specified");
        } else if (isNaN(numberPrice)) {
            return tm.Get("Price must be a valid currency value.");
        } else if (numberPrice < Constants.Min.Price || numberPrice > Constants.Max.Price) {
            return tm.GetWithParams("Price must be between {0} and {1}.", Constants.Min.Price.toString(), Constants.Max.Price.toString());
        } else {
            return "";
        }
    }, [cf, tm])    

    const handleSubmit = React.useCallback(() => {
        var errorMessage = validatePrice(value);
        setValueErrorMessage(errorMessage);
        if (errorMessage === "") {
            onSubmit(lineItem, cf.Parse(value));
        } else {
            messageBox.Show({ message: errorMessage, title: tm.Get("Price") });
        }
    }, [validatePrice, value, onSubmit, lineItem, cf, messageBox, tm]);

    if (!lineItem) {
        return <></>;
    }

    return <>
        <DialogContent>
            <Box display="flex" flexDirection="column">
                <Grid container direction="column" mt={1} rowSpacing={1} justifyItems="stretch">
                    <TextField inputRef={valueInput} value={value} onChange={handleValueChange} fullWidth
                        onFocus={e => e.target.select()}
                        error={valueErrorMessage !== ""}
                        InputProps={{
                            endAdornment: valueErrorMessage !== "" ? <ErrorAdornment validationError={valueErrorMessage} /> : null,
                            inputProps: {
                                style: {
                                    textAlign: 'right',
                                },
                            },
                        }}
                    />
                </Grid>
            </Box>
        </DialogContent>
        <DialogActions>
            <OKCancelControl okCommand={handleSubmit} cancelCommand={onCancel} />
        </DialogActions>
    </>;

}

export default LineItemPrice;